/**
 * The server's clock, learned from response headers.
 *
 * ## Why this exists
 *
 * The Marketing cache is evicted at a wall-clock boundary — 08:00
 * America/Chicago, the deadline every RW-Marketing-Service ingest is scheduled
 * backwards from. That rule has to hold "no matter what", and a rule anchored on
 * `Date.now()` alone does not: a browser clock a day slow keeps serving
 * yesterday's figures forever, and one a day fast revalidates on every read.
 *
 * Every HTTP response carries a `Date` header (RFC 9110 §6.6.1) in GMT, produced
 * by the server — which runs in UTC. Recording the difference between it and the
 * local clock gives an authoritative "now" that survives a wrong local clock,
 * with no extra request and no new endpoint.
 *
 * ## What it is deliberately NOT
 *
 * Not a synchronised clock. Network latency means the offset carries up to a
 * round trip of error, which is irrelevant against a once-a-day boundary and
 * would be unacceptable for anything finer. Nothing here should be used to
 * order events or to measure durations — `Date.now()` differences remain the
 * right tool for that, since they are immune to the offset changing mid-session.
 */
import { logger } from '../utils/logger';

/**
 * Largest offset we are willing to believe, in milliseconds.
 *
 * A misconfigured proxy, a cached response replayed from disk, or a header
 * rewritten by an intermediary can all produce a wildly wrong `Date`. Beyond
 * two days the header is more likely to be wrong than the browser, and adopting
 * it would move the eviction boundary somewhere arbitrary — so it is ignored and
 * the local clock stands. The bound is generous on purpose: a genuinely
 * mis-set browser clock (the case this module exists for) is usually wrong by
 * minutes or hours, not days.
 */
const MAX_TRUSTED_SKEW_MS = 48 * 60 * 60 * 1000;

/**
 * `serverDate - localNow` at the moment the last usable response arrived.
 *
 * Zero until the first response is seen, which makes `serverNow()` identical to
 * `Date.now()` on a cold start — never worse than having no offset at all.
 */
let offsetMs = 0;

/** Whether a skew beyond the trusted bound has already been logged (log once, not per response). */
let skewWarned = false;

/**
 * Learn the offset from a response's `Date` header.
 *
 * Accepts `unknown` because axios types response headers loosely and the header
 * is genuinely absent on some paths (a request served from the browser's own
 * cache, a synthetic response in a test). Anything unparseable is ignored
 * silently: an absent clock is the normal cold-start state, not an error.
 *
 * @param header the raw `Date` header value, if any
 */
export function recordServerDate(header: unknown): void {
  if (typeof header !== 'string' || header.length === 0) return;

  const serverMs = Date.parse(header);
  if (!Number.isFinite(serverMs)) return;

  const skew = serverMs - Date.now();

  if (Math.abs(skew) > MAX_TRUSTED_SKEW_MS) {
    if (!skewWarned) {
      skewWarned = true;
      logger.warn(
        '[serverClock] Ignoring a server Date header more than 48h from the local clock.',
      );
    }
    return;
  }

  offsetMs = skew;
}

/**
 * "Now" according to the server.
 *
 * Falls back to the local clock before any response has been seen. Callers that
 * persist this value should treat it as an instant, not as a monotonic counter —
 * the offset can change between calls when a new response arrives.
 */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

/** The current offset, exposed for diagnostics and for assertions in tests. */
export function serverClockOffsetMs(): number {
  return offsetMs;
}

/**
 * Reset module state.
 *
 * Exported for tests only — the offset is module-level, so one test's header
 * would otherwise leak into the next. Mirrors
 * `resetMasterVersionsStateForTests()` in `dependencies/loadMasterVersions.ts`.
 */
export function resetServerClockForTests(): void {
  offsetMs = 0;
  skewWarned = false;
}
